import { CircleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

import { Field } from '../components/Field.js';
import { Input } from '../components/Input.js';
import type { ApiClientError } from '../lib/apiClient.js';

/**
 * The password inputs plus a checklist driven by the **server's** `details[]`.
 *
 * Plan E6 is explicit that the client must not re-implement the policy, and
 * the reason is not tidiness: the rules live in `password.service.ts` and
 * include a breach list and a reuse history the browser has no access to. A
 * client-side checklist would tell the user their password is fine and then the
 * server would refuse it — at which point the checklist is worse than nothing,
 * because it was believed.
 *
 * So the checklist is a *rendering of the response*: each `details[].code`
 * becomes a line. Codes we have no wording for still appear, using the server's
 * own message, rather than being dropped.
 */
const POLICY_COPY: Record<string, string> = {
  TOO_SHORT: 'Use at least 12 characters.',
  TOO_LONG: 'That is longer than we can store.',
  TOO_COMMON: 'That password is too easy to guess. Choose something less common.',
  REUSED: 'You have used this password before. Choose a new one.',
  NO_LOWERCASE: 'Include a lower-case letter.',
  NO_UPPERCASE: 'Include a capital letter.',
  NO_DIGIT: 'Include a number.',
  NO_SYMBOL: 'Include a symbol.',
  CONTAINS_EMAIL: 'Do not use your email address in your password.',
  MISMATCH: 'The two passwords do not match.',
};

/**
 * Matches the other auth screens. These inputs render on `/password/reset`,
 * `/invite/accept` and `/password/change` and nowhere else — all three are
 * browsed rather than worked in, so the console's 34px row is the wrong size
 * for them and the box takes the heavier border, which is what gives an empty
 * field a visible edge on a tenant whose two surfaces are close together.
 */
const FIELD_CLASS = 'h-10 border-line-strong';

export function policyMessages(error: ApiClientError | null): string[] {
  if (!error || error.code !== 'VALIDATION_FAILED') return [];
  return error.details
    .filter((d) => d.field === undefined || d.field.toLowerCase().includes('password'))
    .map((d) => POLICY_COPY[d.code] ?? d.message);
}

export function PasswordFields({
  error,
  value,
  confirmValue,
  onChange,
  onConfirmChange,
  label = 'New password',
}: {
  error: ApiClientError | null;
  value: string;
  confirmValue: string;
  onChange(next: string): void;
  onConfirmChange(next: string): void;
  label?: string;
}): ReactElement {
  const messages = policyMessages(error);
  const mismatch = confirmValue.length > 0 && confirmValue !== value;

  return (
    <>
      <Field
        label={label}
        required
        hint="At least 12 characters. Your administrator's policy applies."
      >
        {(props) => (
          <Input
            {...props}
            type="password"
            name="newPassword"
            autoComplete="new-password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={FIELD_CLASS}
          />
        )}
      </Field>

      <Field
        label="Confirm new password"
        required
        error={mismatch ? POLICY_COPY.MISMATCH : undefined}
      >
        {(props) => (
          <Input
            {...props}
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
            value={confirmValue}
            onChange={(e) => onConfirmChange(e.target.value)}
            className={FIELD_CLASS}
          />
        )}
      </Field>

      {messages.length > 0 ? (
        /*
          The server's verdict, rendered as a block rather than as loose red
          text: it arrives after a submit, several fields below where the eye
          is, and a bordered plane is what makes it findable. The glyph is the
          redundant channel — colour alone never carries a state (WCAG 1.4.1) —
          and `danger-text` rather than `danger` because §16.6 wants 4.5:1 at
          this size and the pure token measures 4.44:1.

          `data-testid`, the `<ul>`, and one `<li>` per message are unchanged:
          each line is still exactly one `details[]` entry the server sent.
        */
        <div className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border-l-2 border-danger bg-surface-alt p-[var(--space-3)]">
          <CircleAlert
            aria-hidden="true"
            size={16}
            strokeWidth={2}
            className="mt-[2px] shrink-0 text-danger-text"
          />
          <ul
            data-testid="password-policy"
            className="flex flex-col gap-1 text-sm text-danger-text"
          >
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
